/**
 * What a deposit's MAX button resolves to.
 *
 * Generalises `getMaxDepositAmount`: the wallet balance, less a gas reserve when
 * the asset being deposited IS the chain's gas token. Depositing the last of the
 * gas token leaves an account that cannot pay for the transaction that would get
 * it back, so the reserve is held back rather than offered.
 *
 * On chains whose quote token is also the gas token the wallet may hold the
 * asset twice over — a wrapped ERC-20 balance plus the native balance. Only the
 * native part has to fund gas, so `walletUnits` above `nativeUnits` is treated
 * as wrapped and offered whole.
 */
import { Dec, dec } from "../decimal";
import { MaxResult, maxOk, maxUnavailable } from "./types";

export interface DepositMaxInput {
  /** The wallet's balance of the asset, in base units; absent = unread. */
  walletUnits?: bigint;
  /** The wallet's native (gas) balance, in base units. Absent = same as wallet. */
  nativeUnits?: bigint;
  decimals: number;
  /** Whether the asset being deposited is the chain's own gas token. */
  isNativeGasToken: boolean;
  /** How much of the gas token to keep back, as a decimal amount. */
  gasReserve: Dec;
  /** Shown beside the figure: "USDC", "ETH". */
  unit?: string;
}

const clampZero = (value: Dec): Dec => (dec.isNeg(value) ? "0" : value);

export const depositMax = (input: DepositMaxInput): MaxResult => {
  const unit = input.unit ?? "";

  if (input.walletUnits === undefined) {
    return maxUnavailable("balance_unknown");
  }

  const wallet = clampZero(dec.fromUnits(input.walletUnits, input.decimals));

  if (!input.isNativeGasToken) {
    return dec.isZero(wallet)
      ? maxUnavailable("zero_balance")
      : maxOk(wallet, "balance", unit);
  }

  // A misconfigured reserve must not swallow the balance: unreadable means none.
  const reserve = clampZero(dec.parse(input.gasReserve) ?? "0");

  const native =
    input.nativeUnits === undefined
      ? wallet
      : clampZero(dec.fromUnits(input.nativeUnits, input.decimals));
  const wrapped = dec.gt(wallet, native) ? dec.sub(wallet, native) : "0";
  const spendableNative = clampZero(dec.sub(native, reserve));

  const max = dec.add(wrapped, spendableNative);
  if (dec.isZero(max) || dec.isNeg(max)) return maxUnavailable("zero_balance");

  return maxOk(max, dec.lt(max, wallet) ? "gas_reserve" : "balance", unit);
};
